// VCIN Brand: Always use VCIN_COLORS from constants.js - never use Tailwind blue classes
import React, { useState, useEffect } from 'react';
import { 
  MapPin, Plus, Search, ChevronLeft, ChevronRight, X,
  AlertCircle, Loader2, Users, Check, Edit2
} from 'lucide-react';
import api from '../../services/api';
import { VCIN_COLORS } from '../../styles/constants';

// UK Regions for dropdown
const UK_REGIONS = [
  { value: 'LONDON', label: 'London' },
  { value: 'SOUTH_EAST', label: 'South East' },
  { value: 'SOUTH_WEST', label: 'South West' },
  { value: 'EAST_OF_ENGLAND', label: 'East of England' },
  { value: 'WEST_MIDLANDS', label: 'West Midlands' },
  { value: 'EAST_MIDLANDS', label: 'East Midlands' },
  { value: 'YORKSHIRE_AND_HUMBER', label: 'Yorkshire & Humber' },
  { value: 'NORTH_WEST', label: 'North West' },
  { value: 'NORTH_EAST', label: 'North East' },
  { value: 'WALES', label: 'Wales' },
  { value: 'SCOTLAND', label: 'Scotland' },
  { value: 'NORTHERN_IRELAND', label: 'Northern Ireland' }
];

// Table Skeleton
function TableSkeleton() {
  return (
    <tbody className="divide-y divide-gray-100">
      {[...Array(5)].map((_, i) => (
        <tr key={i} className="animate-pulse">
          <td className="px-6 py-4"><div className="h-4 w-32 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-48 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-20 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-24 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-16 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-16 bg-gray-200 rounded" /></td>
        </tr>
      ))}
    </tbody>
  );
}

// Empty State
function EmptyState({ onAdd }) {
  return (
    <div className="p-12 text-center">
      <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-300" />
      <p className="font-medium text-gray-600">No sites yet</p>
      <p className="text-sm text-gray-400 mt-1 mb-4">Add your first site to start assigning officers</p>
      <button
        onClick={onAdd}
        className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
        style={{ backgroundColor: VCIN_COLORS.primary }}
      >
        Add Site
      </button>
    </div>
  );
}

// Officer Selection Component
function OfficerSelector({ allOfficers, selectedIds, onChange, searchTerm, onSearchChange }) {
  const filteredOfficers = allOfficers.filter(officer =>
    officer.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    officer.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleOfficer = (officerId) => {
    if (selectedIds.includes(officerId)) {
      onChange(selectedIds.filter(id => id !== officerId));
    } else {
      onChange([...selectedIds, officerId]);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Assign Officers
      </label>
      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search officers..."
          className="w-full h-10 pl-10 pr-4 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
          data-testid="officer-search"
        />
      </div>
      <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
        {filteredOfficers.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            {allOfficers.length === 0 ? 'No active officers available' : 'No officers match your search'}
          </div>
        ) : (
          filteredOfficers.map(officer => (
            <div
              key={officer.id}
              onClick={() => toggleOfficer(officer.id)}
              className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-0 ${
                selectedIds.includes(officer.id) ? 'bg-blue-50' : ''
              }`}
              data-testid={`officer-option-${officer.id}`}
            >
              <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                selectedIds.includes(officer.id) 
                  ? 'bg-blue-900 border-blue-900' 
                  : 'border-gray-300'
              }`}>
                {selectedIds.includes(officer.id) && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{officer.full_name}</p>
                <p className="text-xs text-gray-500 truncate">{officer.email}</p>
              </div>
            </div>
          ))
        )}
      </div>
      {selectedIds.length > 0 && (
        <p className="mt-2 text-xs text-gray-500">
          {selectedIds.length} officer{selectedIds.length !== 1 ? 's' : ''} selected
        </p>
      )}
    </div>
  );
}

// Slide-in Panel for Add/Edit Site
function SitePanel({ isOpen, onClose, onSuccess, allOfficers, editingSite }) {
  const [formData, setFormData] = useState({
    name: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    postcode: '',
    region: ''
  });
  const [selectedOfficerIds, setSelectedOfficerIds] = useState([]);
  const [officerSearch, setOfficerSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  // Reset form when panel opens/closes or editing site changes
  useEffect(() => {
    if (isOpen) {
      if (editingSite) {
        // Parse address back into components (best effort)
        const addressParts = editingSite.address?.split(', ') || [];
        setFormData({
          name: editingSite.name || '',
          address_line_1: addressParts[0] || '',
          address_line_2: addressParts.length > 2 ? addressParts[1] : '',
          city: addressParts[addressParts.length - 1] || '',
          postcode: editingSite.postcode || '',
          region: editingSite.region || ''
        });
        // Set assigned officers
        const assignedIds = editingSite.assigned_officers?.map(o => o.id) || [];
        setSelectedOfficerIds(assignedIds);
      } else {
        setFormData({
          name: '', address_line_1: '', address_line_2: '',
          city: '', postcode: '', region: ''
        });
        setSelectedOfficerIds([]);
      }
      setOfficerSearch('');
      setError(null);
      setErrors({});
    }
  }, [isOpen, editingSite]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Site name is required';
    if (!formData.address_line_1.trim()) newErrors.address_line_1 = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
    if (!formData.region) newErrors.region = 'Region is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      if (editingSite) {
        // Update existing site
        await api.patch(`/sites/${editingSite.id}`, formData);
        // Update officer assignments
        await api.patch(`/sites/${editingSite.id}/officers`, {
          officer_ids: selectedOfficerIds
        });
      } else {
        // Create new site with officers
        await api.post('/sites', {
          ...formData,
          officer_ids: selectedOfficerIds
        });
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || `Failed to ${editingSite ? 'update' : 'create'} site`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isEditing = !!editingSite;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div 
        className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 overflow-y-auto"
        data-testid="site-panel"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit Site' : 'Add New Site'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="close-site-panel-btn"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.name ? 'border-red-300' : 'border-gray-300'}`}
              placeholder="e.g. Westfield Shopping Centre"
              data-testid="site-name"
            />
            {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1 *</label>
            <input
              type="text"
              value={formData.address_line_1}
              onChange={(e) => setFormData({ ...formData, address_line_1: e.target.value })}
              className={`w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.address_line_1 ? 'border-red-300' : 'border-gray-300'}`}
              data-testid="site-address1"
            />
            {errors.address_line_1 && <p className="mt-1 text-xs text-red-600">{errors.address_line_1}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
            <input
              type="text"
              value={formData.address_line_2}
              onChange={(e) => setFormData({ ...formData, address_line_2: e.target.value })}
              className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
              data-testid="site-address2"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className={`w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.city ? 'border-red-300' : 'border-gray-300'}`}
                data-testid="site-city"
              />
              {errors.city && <p className="mt-1 text-xs text-red-600">{errors.city}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Postcode *</label>
              <input
                type="text"
                value={formData.postcode}
                onChange={(e) => setFormData({ ...formData, postcode: e.target.value.toUpperCase() })}
                className={`w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.postcode ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="SW1A 1AA"
                data-testid="site-postcode"
              />
              {errors.postcode && <p className="mt-1 text-xs text-red-600">{errors.postcode}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Region *</label>
            <select
              value={formData.region}
              onChange={(e) => setFormData({ ...formData, region: e.target.value })}
              className={`w-full h-10 px-3 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errors.region ? 'border-red-300' : 'border-gray-300'}`}
              data-testid="site-region"
            >
              <option value="">Select region...</option>
              {UK_REGIONS.map(region => (
                <option key={region.value} value={region.value}>{region.label}</option>
              ))}
            </select>
            {errors.region && <p className="mt-1 text-xs text-red-600">{errors.region}</p>}
          </div>

          {/* Officer Selection */}
          <OfficerSelector
            allOfficers={allOfficers}
            selectedIds={selectedOfficerIds}
            onChange={setSelectedOfficerIds}
            searchTerm={officerSearch}
            onSearchChange={setOfficerSearch}
          />

          <div className="pt-4 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-11 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 h-11 rounded-lg font-medium text-white flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
              style={{ backgroundColor: VCIN_COLORS.primary }}
              data-testid="submit-site-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isEditing ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                isEditing ? 'Save Changes' : 'Create Site'
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

export default function SitesPage() {
  const [sites, setSites] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [showPanel, setShowPanel] = useState(false);
  const [editingSite, setEditingSite] = useState(null);

  const fetchSites = async (page = 1) => {
    try {
      setLoading(true);
      const [sitesRes, officersRes] = await Promise.all([
        api.get(`/sites?page=${page}&limit=10`),
        api.get('/officers?limit=1000') // Get all officers to cross-reference
      ]);
      setSites(sitesRes.data.sites);
      setPagination(sitesRes.data.pagination);
      setOfficers(officersRes.data.officers || []);
    } catch (err) {
      setError(err.message || 'Failed to load sites');
    } finally {
      setLoading(false);
    }
  };

  // Get active officers for selection
  const activeOfficers = officers.filter(o => o.status === 'ACTIVE');

  // Get officer names for a specific site
  const getOfficersForSite = (siteId) => {
    return officers.filter(officer => 
      officer.assigned_sites?.some(site => 
        (site.id || site) === siteId
      )
    );
  };

  // Handle edit site click
  const handleEditSite = async (site) => {
    try {
      // Fetch full site data including assigned officers
      const response = await api.get(`/sites/${site.id}`);
      setEditingSite(response.data.site);
      setShowPanel(true);
    } catch (err) {
      console.error('Failed to load site details:', err);
      // Fallback to basic site data
      setEditingSite(site);
      setShowPanel(true);
    }
  };

  // Handle add new site
  const handleAddSite = () => {
    setEditingSite(null);
    setShowPanel(true);
  };

  // Handle panel close
  const handleClosePanel = () => {
    setShowPanel(false);
    setEditingSite(null);
  };

  useEffect(() => {
    fetchSites();
  }, []);

  const filteredSites = sites.filter(site =>
    site.name?.toLowerCase().includes(search.toLowerCase()) ||
    site.city?.toLowerCase().includes(search.toLowerCase()) ||
    site.postcode?.toLowerCase().includes(search.toLowerCase())
  );

  const formatRegion = (region) => {
    return region?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) || '-';
  };

  if (error && !loading) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <span className="text-red-700">{error}</span>
      </div>
    );
  }

  return (
    <div data-testid="sites-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Sites</h1>
        <button
          onClick={handleAddSite}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-white transition-colors hover:opacity-90"
          style={{ backgroundColor: VCIN_COLORS.primary }}
          data-testid="add-site-btn"
        >
          <Plus className="w-4 h-4" />
          Add Site
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, city, or postcode..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-10 pl-10 pr-4 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
            data-testid="site-search"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="sites-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Site Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Address</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Postcode</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Region</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Officers</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            
            {loading ? (
              <TableSkeleton />
            ) : filteredSites.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan="6">
                    <EmptyState onAdd={handleAddSite} />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="divide-y divide-gray-100">
                {filteredSites.map((site) => {
                  const siteOfficers = getOfficersForSite(site.id);
                  return (
                    <tr key={site.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{site.name}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">
                          {site.address || (
                            <>
                              {site.address_line_1}
                              {site.address_line_2 && <span>, {site.address_line_2}</span>}
                              <span>, {site.city}</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-mono text-sm text-gray-600">{site.postcode}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatRegion(site.region)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-start gap-1.5 text-sm">
                          <Users className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="text-gray-600">
                            {siteOfficers.length === 0 ? (
                              <span className="italic text-gray-400">No officers assigned</span>
                            ) : (
                              <span>{siteOfficers.map(o => o.full_name).join(', ')}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleEditSite(site)}
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          data-testid={`edit-site-${site.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * 10 + 1} to {Math.min(pagination.page * 10, pagination.total)} of {pagination.total} sites
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchSites(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 px-2">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => fetchSites(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Site Panel (Add/Edit) */}
      <SitePanel
        isOpen={showPanel}
        onClose={handleClosePanel}
        onSuccess={() => fetchSites(pagination.page)}
        allOfficers={activeOfficers}
        editingSite={editingSite}
      />
    </div>
  );
}
